import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Clock, MapPin, Filter, X, Edit, CheckCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const VISIT_STATUSES = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const EDITABLE_STATUSES = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

export default function Visits() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  
  const isAdmin = user?.role === 'ADMIN';
  
  const [filters, setFilters] = useState({
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    status: searchParams.get('status') || '',
    cleanerId: searchParams.get('cleanerId') || ''
  });

  const [formData, setFormData] = useState({
    client_id: '',
    location_id: '',
    assigned_cleaner_id: '',
    scheduled_start: '',
    estimated_duration_minutes: 60,
    notes: ''
  });

  const [editFormData, setEditFormData] = useState({
    assigned_cleaner_id: null,
    scheduled_start: '',
    estimated_duration_minutes: 60,
    notes: '',
    status: '',
    approved_minutes: '',
    approval_note: ''
  });
  
  const [cleanerTouched, setCleanerTouched] = useState(false);

  const fetchVisits = useCallback(async (currentFilters) => {
    try {
      const params = new URLSearchParams();
      if (currentFilters.dateFrom) params.append('date_from', currentFilters.dateFrom);
      if (currentFilters.dateTo) params.append('date_to', currentFilters.dateTo);
      if (currentFilters.status) params.append('status', currentFilters.status);
      if (currentFilters.cleanerId) params.append('cleaner_id', currentFilters.cleanerId);
      
      const url = `${API_URL}/api/visits${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setVisits(response.data);
    } catch (error) {
      toast.error('Failed to fetch visits');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [clientsRes, cleanersRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients?is_active=true`),
        axios.get(`${API_URL}/api/users/cleaners`)
      ]);
      setClients(clientsRes.data);
      setCleaners(cleanersRes.data);
    } catch (error) {
      toast.error('Failed to fetch reference data');
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchVisits(filters);
  }, [filters, fetchVisits]);

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    const params = new URLSearchParams();
    if (newFilters.dateFrom) params.set('dateFrom', newFilters.dateFrom);
    if (newFilters.dateTo) params.set('dateTo', newFilters.dateTo);
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.cleanerId) params.set('cleanerId', newFilters.cleanerId);
    setSearchParams(params);
  };

  const clearFilters = () => {
    updateFilters({ dateFrom: '', dateTo: '', status: '', cleanerId: '' });
  };

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.status || filters.cleanerId;

  const fetchLocationsByClient = async (clientId) => {
    if (!clientId) {
      setLocations([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/locations/client/${clientId}`);
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    }
  };

  const handleClientChange = (clientId) => {
    setFormData({ ...formData, client_id: clientId, location_id: '' });
    fetchLocationsByClient(clientId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        scheduled_start: new Date(formData.scheduled_start).toISOString(),
        assigned_cleaner_id: formData.assigned_cleaner_id || null
      };
      await axios.post(`${API_URL}/api/visits`, submitData);
      toast.success('Visit created successfully');
      setDialogOpen(false);
      setFormData({
        client_id: '',
        location_id: '',
        assigned_cleaner_id: '',
        scheduled_start: '',
        estimated_duration_minutes: 60,
        notes: ''
      });
      setLocations([]);
      fetchVisits(filters);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create visit');
    }
  };

  const handleEditVisit = (visit) => {
    setSelectedVisit(visit);
    const scheduledDate = new Date(visit.scheduled_start);
    const localDatetime = format(scheduledDate, "yyyy-MM-dd'T'HH:mm");
    
    setEditFormData({
      assigned_cleaner_id: visit.assigned_cleaner_id || null,
      scheduled_start: localDatetime,
      estimated_duration_minutes: visit.estimated_duration_minutes,
      notes: visit.notes || '',
      status: visit.status,
      approved_minutes: visit.approved_minutes ?? '',
      approval_note: visit.approval_note || ''
    });
    setCleanerTouched(false);
    setEditDialogOpen(true);
  };

  const handleCleanerChange = (value) => {
    setCleanerTouched(true);
    // 'unassigned' sentinel value means empty string (which backend accepts for unassigning)
    const cleanerId = (value === 'unassigned') ? '' : value;
    setEditFormData(prev => ({ ...prev, assigned_cleaner_id: cleanerId }));
  };

  const handleUpdateVisit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        scheduled_start: new Date(editFormData.scheduled_start).toISOString(),
        estimated_duration_minutes: editFormData.estimated_duration_minutes,
        notes: editFormData.notes
      };
      
      // Always include assigned_cleaner_id - send empty string for unassigned
      // The backend accepts "" as unassigned
      updateData.assigned_cleaner_id = editFormData.assigned_cleaner_id ?? '';
      
      // Only include status if it changed to avoid triggering validation
      if (editFormData.status !== selectedVisit.status) {
        updateData.status = editFormData.status;
      }
      
      await axios.patch(`${API_URL}/api/visits/${selectedVisit.id}`, updateData);
      toast.success('Visit updated successfully');
      setEditDialogOpen(false);
      setSelectedVisit(null);
      setCleanerTouched(false);
      fetchVisits(filters);
    } catch (error) {
      console.error('Update visit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update visit');
    }
  };

  const handleSaveApproval = async () => {
    if (selectedVisit.status === 'IN_PROGRESS') {
      toast.error('Cannot approve time while visit is in progress. Ask cleaner to finish first.');
      return;
    }
    
    const approvedMinutes = parseInt(editFormData.approved_minutes);
    if (isNaN(approvedMinutes) || approvedMinutes < 0) {
      toast.error('Please enter a valid number of minutes (0 or greater)');
      return;
    }
    if (approvedMinutes > 1440) {
      toast.error('Approved minutes cannot exceed 24 hours (1440 minutes)');
      return;
    }
    
    try {
      await axios.patch(`${API_URL}/api/visits/${selectedVisit.id}`, {
        approved_minutes: approvedMinutes,
        approval_note: editFormData.approval_note || null
      });
      toast.success('Time approval saved');
      
      // Refresh visit data
      const response = await axios.get(`${API_URL}/api/visits/${selectedVisit.id}`);
      setSelectedVisit(response.data);
      fetchVisits(filters);
    } catch (error) {
      console.error('Save approval error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save approval');
    }
  };

  const handleApproveScheduled = () => {
    setEditFormData({
      ...editFormData,
      approved_minutes: selectedVisit.estimated_duration_minutes.toString()
    });
  };

  const handleApproveActual = () => {
    setEditFormData({
      ...editFormData,
      approved_minutes: (selectedVisit.actual_minutes || 0).toString()
    });
  };

  const getAllowedStatuses = (currentStatus) => {
    if (currentStatus === 'IN_PROGRESS') {
      return [{ value: 'IN_PROGRESS', label: 'In Progress (cannot change)' }];
    }
    const transitions = {
      'SCHEDULED': ['SCHEDULED', 'DONE', 'CANCELLED'],
      'DONE': ['DONE', 'SCHEDULED'],
      'CANCELLED': ['CANCELLED', 'SCHEDULED']
    };
    const allowed = transitions[currentStatus] || [];
    return EDITABLE_STATUSES.filter(s => allowed.includes(s.value));
  };

  const openDeleteDialog = (visit) => {
    setVisitToDelete(visit);
    setDeleteDialogOpen(true);
  };

  const handleDeleteVisit = async () => {
    if (!visitToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/visits/${visitToDelete.id}`);
      toast.success('Visit deleted successfully');
      setDeleteDialogOpen(false);
      setVisitToDelete(null);
      fetchVisits(filters);
    } catch (error) {
      console.error('Delete visit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete visit');
    }
  };

  return (
    <Layout>
      <div className="space-y-4 md:space-y-6 overflow-x-hidden" data-testid="visits-page">
        {/* Header */}
        <div className="flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 w-full sm:w-auto" data-testid="add-visit-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Visit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Visit</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="visit-form">
                <div>
                  <Label htmlFor="client_id">Client</Label>
                  <Select value={formData.client_id || undefined} onValueChange={handleClientChange}>
                    <SelectTrigger data-testid="visit-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.filter(c => c.id).map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="location_id">Location</Label>
                  <Select
                    value={formData.location_id || undefined}
                    onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                    disabled={!formData.client_id}
                  >
                    <SelectTrigger data-testid="visit-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.filter(l => l.id).map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.address_line1}, {location.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assigned_cleaner_id">Assigned Cleaner (Optional)</Label>
                  <Select
                    value={formData.assigned_cleaner_id || undefined}
                    onValueChange={(value) => setFormData({ ...formData, assigned_cleaner_id: value })}
                  >
                    <SelectTrigger data-testid="visit-cleaner-select">
                      <SelectValue placeholder="Leave unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {cleaners.filter(c => c.id).map((cleaner) => (
                        <SelectItem key={cleaner.id} value={cleaner.id}>
                          {cleaner.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="scheduled_start">Scheduled Start</Label>
                  <Input
                    id="scheduled_start"
                    type="datetime-local"
                    value={formData.scheduled_start}
                    onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                    required
                    data-testid="visit-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="estimated_duration_minutes">Duration (minutes)</Label>
                  <Input
                    id="estimated_duration_minutes"
                    type="number"
                    value={formData.estimated_duration_minutes}
                    onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: parseInt(e.target.value) })}
                    required
                    min="15"
                    step="15"
                    data-testid="visit-duration-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="visit-notes-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-visit-button">
                  Create Visit
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Controls */}
        <div className="bg-white border border-slate-200 p-3 sm:p-4" data-testid="visits-filters">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Filters</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-slate-500 hover:text-slate-700" data-testid="clear-filters-btn">
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="filter-date-from" className="text-xs text-slate-500">From Date</Label>
              <Input id="filter-date-from" type="date" value={filters.dateFrom} onChange={(e) => updateFilters({ ...filters, dateFrom: e.target.value })} className="mt-1" data-testid="filter-date-from" />
            </div>
            <div>
              <Label htmlFor="filter-date-to" className="text-xs text-slate-500">To Date</Label>
              <Input id="filter-date-to" type="date" value={filters.dateTo} onChange={(e) => updateFilters({ ...filters, dateTo: e.target.value })} className="mt-1" data-testid="filter-date-to" />
            </div>
            <div>
              <Label htmlFor="filter-status" className="text-xs text-slate-500">Status</Label>
              <Select value={filters.status || undefined} onValueChange={(value) => updateFilters({ ...filters, status: value })}>
                <SelectTrigger className="mt-1" data-testid="filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {VISIT_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="filter-cleaner" className="text-xs text-slate-500">Cleaner</Label>
              <Select value={filters.cleanerId || undefined} onValueChange={(value) => updateFilters({ ...filters, cleanerId: value })}>
                <SelectTrigger className="mt-1" data-testid="filter-cleaner">
                  <SelectValue placeholder="All cleaners" />
                </SelectTrigger>
                <SelectContent>
                  {cleaners.filter(c => c.id).map((cleaner) => (
                    <SelectItem key={cleaner.id} value={cleaner.id}>{cleaner.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : visits.length === 0 ? (
          <div className="bg-white border border-slate-200 px-4 py-8 text-center text-slate-500">
            No visits found{hasActiveFilters ? ' matching your filters' : ''}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {visits.map((visit) => (
                <div 
                  key={visit.id} 
                  className="bg-white border border-slate-200 p-4"
                  data-testid={`visit-card-${visit.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">{visit.client_name}</h3>
                        {visit.approved_minutes !== null && visit.approved_minutes !== undefined && (
                          <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" title="Time Approved" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{visit.location_city}, {visit.location_postcode}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-bold border flex-shrink-0 ml-2 ${
                      visit.status === 'SCHEDULED' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      visit.status === 'IN_PROGRESS' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      visit.status === 'DONE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      visit.status === 'CANCELLED' ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-slate-100 text-slate-700 border-slate-200'
                    }`}>
                      {visit.status.replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span>{format(new Date(visit.scheduled_start), 'MMM dd, HH:mm')}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Duration:</span> {visit.estimated_duration_minutes}min
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">Cleaner:</span>{' '}
                      {visit.assigned_cleaner_name || <span className="text-slate-400">Unassigned</span>}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditVisit(visit)} 
                      className="flex-1"
                      data-testid={`edit-visit-mobile-${visit.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openDeleteDialog(visit)}
                        className="border-red-200 text-red-600 hover:bg-red-50 px-3"
                        data-testid={`delete-visit-mobile-${visit.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Date & Time</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Assigned To</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {visits.map((visit) => (
                      <tr key={visit.id} data-testid={`visit-row-${visit.id}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          {visit.client_name}
                          {visit.approved_minutes !== null && visit.approved_minutes !== undefined && (
                            <CheckCircle className="w-3 h-3 text-emerald-500 inline ml-1" title="Time Approved" />
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{visit.location_city}, {visit.location_postcode}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{format(new Date(visit.scheduled_start), 'MMM dd, HH:mm')}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {visit.estimated_duration_minutes}min
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {visit.assigned_cleaner_name || <span className="text-slate-400">Unassigned</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-bold border ${
                            visit.status === 'SCHEDULED' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            visit.status === 'IN_PROGRESS' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            visit.status === 'DONE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            visit.status === 'CANCELLED' ? 'bg-red-50 text-red-700 border-red-200' :
                            'bg-slate-100 text-slate-700 border-slate-200'
                          }`}>
                            {visit.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleEditVisit(visit)} data-testid={`edit-visit-${visit.id}`}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            {isAdmin && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => openDeleteDialog(visit)}
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                data-testid={`delete-visit-${visit.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Visit</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this visit for "{visitToDelete?.client_name}"? This action will permanently remove it and cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteVisit}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete Visit
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Visit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Visit</DialogTitle>
            </DialogHeader>
            {selectedVisit && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                  <p className="font-semibold text-slate-900">{selectedVisit.client_name}</p>
                  <p className="text-sm text-slate-600">
                    {selectedVisit.location_address}, {selectedVisit.location_city} {selectedVisit.location_postcode}
                  </p>
                </div>
                
                <form onSubmit={handleUpdateVisit} className="space-y-4" data-testid="edit-visit-form">
                  <div>
                    <Label htmlFor="edit_assigned_cleaner_id">Assigned Cleaner</Label>
                    <Select
                      value={editFormData.assigned_cleaner_id || 'unassigned'}
                      onValueChange={handleCleanerChange}
                    >
                      <SelectTrigger data-testid="edit-visit-cleaner-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {cleaners.filter(c => c.id).map((cleaner) => (
                          <SelectItem key={cleaner.id} value={cleaner.id}>{cleaner.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit_scheduled_start">Scheduled Start</Label>
                    <Input id="edit_scheduled_start" type="datetime-local" value={editFormData.scheduled_start} onChange={(e) => setEditFormData({ ...editFormData, scheduled_start: e.target.value })} required data-testid="edit-visit-start-input" />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_estimated_duration_minutes">Duration (minutes)</Label>
                    <Input id="edit_estimated_duration_minutes" type="number" value={editFormData.estimated_duration_minutes} onChange={(e) => setEditFormData({ ...editFormData, estimated_duration_minutes: parseInt(e.target.value) })} required min="15" step="15" data-testid="edit-visit-duration-input" />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_status">Status</Label>
                    <Select value={editFormData.status} onValueChange={(value) => setEditFormData({ ...editFormData, status: value })} disabled={selectedVisit.status === 'IN_PROGRESS'}>
                      <SelectTrigger data-testid="edit-visit-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getAllowedStatuses(selectedVisit.status).map((status) => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedVisit.status === 'IN_PROGRESS' && (
                      <p className="text-xs text-amber-600 mt-1">Visit is in progress. Ask cleaner to finish first.</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_notes">Notes</Label>
                    <Textarea id="edit_notes" value={editFormData.notes} onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })} rows={2} data-testid="edit-visit-notes-input" />
                  </div>
                  
                  <div className="flex gap-3 pt-4 border-t border-slate-200">
                    <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="update-visit-button">Update Visit</Button>
                  </div>
                </form>

                {/* Time Approval Section */}
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Time Approval
                  </h3>
                  
                  {selectedVisit.status === 'IN_PROGRESS' ? (
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 border border-amber-200 rounded">
                      Cannot approve time while visit is in progress. Ask cleaner to finish first.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="p-2 bg-slate-50 border border-slate-200 rounded">
                          <p className="text-xs text-slate-500">Scheduled</p>
                          <p className="font-semibold text-slate-900">{selectedVisit.estimated_duration_minutes} min</p>
                        </div>
                        <div className="p-2 bg-slate-50 border border-slate-200 rounded">
                          <p className="text-xs text-slate-500">Actual</p>
                          <p className="font-semibold text-slate-900">{selectedVisit.actual_minutes || 0} min</p>
                        </div>
                        <div className={`p-2 border rounded ${selectedVisit.approved_minutes !== null && selectedVisit.approved_minutes !== undefined ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                          <p className="text-xs text-slate-500">Approved</p>
                          <p className="font-semibold text-slate-900">
                            {selectedVisit.approved_minutes !== null && selectedVisit.approved_minutes !== undefined 
                              ? `${selectedVisit.approved_minutes} min` 
                              : '—'}
                          </p>
                        </div>
                      </div>
                      
                      {selectedVisit.approved_by_name && (
                        <p className="text-xs text-slate-500">
                          Approved by {selectedVisit.approved_by_name} on {format(new Date(selectedVisit.approved_at), 'MMM dd, HH:mm')}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={handleApproveScheduled} data-testid="approve-scheduled-btn">
                          Approve Scheduled
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={handleApproveActual} data-testid="approve-actual-btn">
                          Approve Actual
                        </Button>
                      </div>
                      
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label htmlFor="approved_minutes" className="text-xs">Approved Minutes</Label>
                          <Input
                            id="approved_minutes"
                            type="number"
                            min="0"
                            max="1440"
                            value={editFormData.approved_minutes}
                            onChange={(e) => setEditFormData({ ...editFormData, approved_minutes: e.target.value })}
                            placeholder="Enter minutes"
                            data-testid="approved-minutes-input"
                          />
                        </div>
                        <Button type="button" onClick={handleSaveApproval} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-approval-btn">
                          Save Approval
                        </Button>
                      </div>
                      
                      <div>
                        <Label htmlFor="approval_note" className="text-xs">Approval Note (optional)</Label>
                        <Textarea
                          id="approval_note"
                          value={editFormData.approval_note}
                          onChange={(e) => setEditFormData({ ...editFormData, approval_note: e.target.value })}
                          rows={2}
                          placeholder="Add a note about this approval..."
                          data-testid="approval-note-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
